<template>
  <b-container>
    <tool-header
      tool-name="String Override Tool"
      tool-icon-text="Override localized strings in the MLB app"
      class="mb-3">
      <slot name="header" />
    </tool-header>
    <b-row
      v-if="canStringOverrideEdit"
      class="mt-2">
      <b-col id="beta-publish-button-wrapper">
        <b-btn
          :disabled="!enableBetaPublish"
          variant="primary"
          class="w-100"
          @click="onPublishToBeta">
          Publish to {{ betaEnvName }}
        </b-btn>
        <b-tooltip
          :disabled="enableBetaPublish"
          target="beta-publish-button-wrapper"
          title="No changes have yet been made. Please make at least one change to enable" />
      </b-col>
      <b-col id="prod-publish-button-wrapper">
        <b-btn
          v-if="canStringOverridePublish"
          :disabled="!enableProductionPublish || enableBetaPublish"
          variant="primary"
          class="w-100"
          @click.prevent="showPublishModal = true">
          Publish to {{ liveEnvName }}
        </b-btn>
        <b-tooltip
          v-if="enableBetaPublish"
          target="prod-publish-button-wrapper"
          :title="'Please publish unsaved changes to ' + betaEnvName + ' first to enable'" />
        <b-tooltip
          v-else-if="!enableProductionPublish"
          target="prod-publish-button-wrapper"
          :title="'No unpublished changes currently exist in ' + betaEnvName + '. Publish changes first to enable'" />
      </b-col>
    </b-row>
    <b-row
      class="my-2">
      <b-col cols="5">
        <b-input
          v-model="filterText"
          type="search"
          placeholder="Search strings..." />
      </b-col>
    </b-row>
    <b-modal
      v-if="canStringOverridePublish"
      v-model="showPublishModal"
      title="Review Staged Changes"
      size="xl"
      hide-footer>
      <json-viewer
        :value="newOverrides"
        :expand-depth="2" />
      <b-form
        class="mt-3"
        @submit.prevent="onPublishToProd"
        @reset="showPublishModal = false">
        <b-btn
          type="submit"
          variant="primary"
          class="ml-2 xl-wide float-right">
          Publish
        </b-btn>
        <b-btn
          type="reset"
          variant="outline-secondary"
          class="xl-wide float-right">
          Cancel
        </b-btn>
      </b-form>
    </b-modal>
    <div>
      <b-table
        id="stringsTable"
        :items="items"
        :filter="filterText"
        :fields="fields"
        :per-page="perPage"
        :current-page="currentPage">
        <template #cell(label)="data">
          <span>{{ data.value }}</span>
        </template>
        <template #cell(english)="data">
          <b-form-input
            v-if="items[data.index].isEdit"
            v-model="items[data.index].english"
            type="text"
            style="white-space: pre;" />
          <span v-else>{{ data.value }}</span>
        </template>
        <template #cell(spanish)="data">
          <b-form-input
            v-if="items[data.index].isEdit"
            v-model="items[data.index].spanish"
            type="text"
            style="white-space: pre;" />
          <span v-else>{{ data.value }}</span>
        </template>
        <template #cell(edit)="data">
          <b-button @click="editRowHandler(data)">
            <span v-if="!items[data.index].isEdit">Edit</span>
            <span v-else>Done</span>
          </b-button>
        </template>
      </b-table>
      <b-pagination
        v-model="currentPage"
        :total-rows="rows"
        :per-page="perPage"
        aria-controls="stringsTable" />
    </div>
  </b-container>
</template>
<script>

import StringOverride from '@/services/api/StringOverride'
import {mapGetters} from "vuex"
import ToolHeader from '../components/ToolHeader.vue'
import JsonViewer from 'vue-json-viewer'

export default {
  name: "StringOverride",
  components: {ToolHeader, JsonViewer},
  data() {
    return {
      items: [],
      showPublishModal: false,
      editingOverrides : {},
      newOverrides : {},
      perPage: 20,
      currentPage: 1,
      enableBetaPublish: false,
      enableProductionPublish: false,
      filterText: '',
      fields: [
        { key: "label", label: "Label"},
        { key: "english", label: "English" },
        { key: "spanish", label: "Spanish" },
        { key: 'edit', label: ''},
      ],
    }
  },
  computed: {
    rows(){
      return this.items.length
    },
    ...mapGetters([
      'canStringOverrideEdit',
      'canStringOverridePublish',
      'betaEnvName',
      'liveEnvName',
    ]),
  },
  async mounted() {
      this.items = await StringOverride.getLocalizedStrings()
      this.items = this.items.map(item => ({...item, isEdit: false}))
  },
  methods: {
      hasRowChanged(data, overrideEntry) {
        const originalEntry = this.editingOverrides[data.item.label]
        if(!originalEntry){
          return false
        }
        if(originalEntry.en != overrideEntry.en){
          return true
        }
        if(originalEntry.es != overrideEntry.es){
          return true
        }
        return false
      },
      editRowHandler(data) {
        const overrideEntry = {'en': data.item.english, 'es': data.item.spanish}
        if(!this.items[data.index].isEdit){
          //when edit is pressed, capture the original state of the string so we can detect changes and only publish modified strings
          this.editingOverrides[data.item.label] = overrideEntry
        }else{
          if(this.hasRowChanged(data, overrideEntry)){
            this.newOverrides[data.item.label] = overrideEntry
            this.enableBetaPublish = true
          }
        }
        
        this.items[data.index].isEdit = !this.items[data.index].isEdit
      },
      getIndexBasedOnCurrentPage(index) {
        return index + (this.currentPage-1)*this.perPage
      },
      async onPublishToBeta() {
        await StringOverride.postNewOverrides(this.newOverrides)
        this.enableProductionPublish = true
        this.enableBetaPublish = false
        this.$alert.success(`New string overrides published successfully to ${this.betaEnvName}`)
      },
      async onPublishToProd() {
        await StringOverride.promoteToProd()
        this.showPublishModal = false
        this.$alert.success(`New string overrides published successfully to ${this.liveEnvName}`)
        this.enableProductionPublish = false
      },
    },
  }
</script>